import angular from 'angular';
import messages from 'angular-messages';
import sanitize from 'angular-sanitize';
import vsRepeat from 'angular-vs-repeat';

import i18n from '../i18n';
import uuid from '../uuid';
import indeterminateProgress from '../indeterminate-progress';

import akamTree from './tree-directive';

/**
 * @ngdoc module
 * @name akamai.components.tree
 *
 * @guideline https://design.akamai.com/components/tree/
 *
 * @requires module:ngModel
 * @requires module:ngSanitize
 * @requires module:ngMessages
 *
 * @description
 * The tree component is used to display large amounts of data in a tree structure.
 * Tree nodes may be expanded or collapsed if the node contains children items.
 * On-select callback gets fired when a tree node is clicked.
 *
 * In order to change the height of the tree, override the following two styles:
 *
 * .akam-tree-container .akam-tree-viewport {
 *   height: 200px !important;
 * }
 *
 * .akam-tree-container .akam-filter-viewport {
 *   height: 200px !important;
 * }
 *
 *
 * @example tree.html
 * {% raw %}
 * <akam-tree
 *   items="vm.ex1.noItems"
 *   is-collapsed="vm.ex1.isCollapsed"
 *   id-property="id"
 *   text-property="name"
 *   children-property="children"
 *   filter-placeholder="components.name"
 *   on-select="vm.ex1.onSelectCallback(item)"
 *   loading-message="components.name">
 * </akam-tree>
 * {% endraw %}
 *
 * {% raw %}
 * <akam-tree
 *   items="vm.ex1.noItems"
 *   is-collapsed="vm.ex1.isCollapsed"
 *   id-property="id"
 *   text-property="name"
 *   children-property="children"
 *   filter-placeholder="components.name"
 *   on-select="vm.ex1.onSelectCallback(item)"
 *   loading-message="components.name">
 *
 *   <div class="custom-tree">
 *     <i ng-class="::{'aci-folder': item.children.length > 0,
 *                     'aci-docx': !item.children || item.children.length === 0}"></i>
 *     <span>custom:: {{ ::item.name }} - {{ ::vm.ex1.customMarkup }}</span>
 *   </div>
 * </akam-tree>
 * {% endraw %}
 */
export default angular.module('akamai.components.tree', [
  sanitize,
  messages,
  vsRepeat,
  i18n.name,
  uuid.name,
  indeterminateProgress.name
])

/**
 * @ngdoc directive
 * @name akamTree
 * @description Creates a tree view control
 *
 * @restrict E
 *
 * @param {Object[] | Promise} [items] Required data model for the tree. Will display a loading
 * indicator if it is a promise.
 *
 * @param {string} [id-property=id] Used to determine object equality.
 * An id must be unique per tree node.
 *
 * @param {string} [text-property] A shorthand method that can be used if custom markup is not
 * necessary. If provided, will be used for rendering and filtering.
 * Text property is required even when custom markup is provided.
 *
 * @param {string} [children-property] Items property that represents child nodes
 *
 * @param {function(Object, string) : boolean} [on-filter] Callback that is invoked
 * whenever a filter takes place. If provided, overrides textProperty when filtering tree.
 * Will be passed an individual item and the search term. Must return true if the item
 * should be included in the result set and false if not.
 *
 * @param {function(Object[])} [on-select] Callback function when item is selected. Will be passed
 * the selected item
 *
 * @param {Boolean} [is-collapsed] If true, tree will be collapsed,
 * otherwise all nodes will be expanded
 *
 * @param {*} [not-filterable] The presence of this attribute will disable filtering for the entire
 * tree. It will have the effect of hiding the filter input box.
 *
 * @param {string|TranslateKey} [filter-placeholder] Text for the placeholder attribute on the
 * filter bar.
 *
 * @param {string|TranslateKey} [no-filter-results-message] Text that should display when a filter
 * returns zero results.
 *
 * @param {string|TranslateKey} [loading-message] Text that should display under the loading
 * indicator if the `items` attribute is a promise.
 *
 * @param {string|TranslateKey} [failure-message] Text that should display if `items` is a promise
 * and it fails to load.
 *
 * @param {string|TranslateKey} [no-items-message] Text that should display when there are no items
 * to display.
 */
  .directive('akamTree', akamTree);